/**
 * Hyperliquid Asset Metadata Adapter
 *
 * Implements PerpsAssetMetaAdapter. Uses a module-level singleton cache
 * (same pattern as the useHLAssetMeta hook) with 5-min TTL and
 * inflight-request deduplication.
 *
 * Supports multi-dex: fetches main dex + each HIP-3 dex meta in parallel,
 * merging into one asset map keyed by HL coin name (e.g. "BTC", "xyz:TSLA").
 */

import { HyperliquidInfoClient } from "../client";
import type { HLMeta, HLAssetInfo } from "../client";
import type { PerpsAssetMetaAdapter } from "./types";

// ============================================================================
// Constants
// ============================================================================

const CACHE_TTL_MS = 5 * 60 * 1000;
const DEFAULT_MAX_LEVERAGE = 50;
const DEFAULT_SZ_DECIMALS = 4;

/** Strip quote asset suffix (e.g. "BTCUSDT" → "BTC") to match HL coin names */
function stripQuote(symbol: string): string {
  for (const q of ["USDT", "USDC", "BUSD"]) {
    if (symbol.endsWith(q)) return symbol.slice(0, -q.length);
  }
  return symbol;
}

// ============================================================================
// Module-level Singleton Cache
// ============================================================================

let cachedAssetMap: Map<string, HLAssetInfo> | null = null;
let cacheTimestamp = 0;
let inflightRequest: Promise<void> | null = null;

async function fetchAllMeta(
  client: HyperliquidInfoClient,
  dexNames: string[],
): Promise<void> {
  const now = Date.now();
  if (cachedAssetMap && now - cacheTimestamp < CACHE_TTL_MS) return;
  if (inflightRequest) return inflightRequest;

  inflightRequest = (async () => {
    try {
      // Fetch main dex + each HIP-3 dex in parallel
      const promises: Promise<HLMeta>[] = [client.getMeta()];
      for (const dex of dexNames) {
        promises.push(client.getMeta(dex));
      }
      const results = await Promise.all(promises);

      const assetMap = new Map<string, HLAssetInfo>();

      // Main dex assets (keyed by name, e.g. "BTC")
      for (const asset of results[0]!.universe) {
        assetMap.set(asset.name, asset);
      }

      // HIP-3 dex assets (keyed by full name, e.g. "xyz:TSLA")
      for (let i = 0; i < dexNames.length; i++) {
        for (const asset of results[i + 1]!.universe) {
          assetMap.set(asset.name, asset);
        }
      }

      cachedAssetMap = assetMap;
      cacheTimestamp = Date.now();
    } finally {
      inflightRequest = null;
    }
  })();

  return inflightRequest;
}

// ============================================================================
// Adapter Class
// ============================================================================

export class HyperliquidAssetMeta implements PerpsAssetMetaAdapter {
  private restClient: HyperliquidInfoClient;
  private dexNames: string[];

  constructor(
    testnet = false,
    dexNames?: string[],
    sharedClient?: HyperliquidInfoClient,
  ) {
    this.restClient =
      sharedClient ?? new HyperliquidInfoClient({ testnet, dexNames });
    this.dexNames = dexNames ?? [];
  }

  async initialize(): Promise<void> {
    await fetchAllMeta(this.restClient, this.dexNames);
  }

  getMaxLeverage(symbol: string): number {
    if (cachedAssetMap) {
      const asset =
        cachedAssetMap.get(symbol) ?? cachedAssetMap.get(stripQuote(symbol));
      if (asset) return asset.maxLeverage;
    }
    return DEFAULT_MAX_LEVERAGE;
  }

  getSzDecimals(symbol: string): number {
    if (cachedAssetMap) {
      const asset =
        cachedAssetMap.get(symbol) ?? cachedAssetMap.get(stripQuote(symbol));
      if (asset) return asset.szDecimals;
    }
    return DEFAULT_SZ_DECIMALS;
  }
}
